import { useTranslation } from 'react-i18next';
import { EditableLayout, Notification, Prompt } from 'Components';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import { getAppRoutes } from 'Pages/App/App.constants';
import { schema } from '../schema';
import { useMutation } from '@apollo/client';
import { CREATE_USER } from 'Services/Api/Users/Mutations';
import { useHistory } from 'react-router';
import CreateUserFormFields from './CreateUserFormFields';
import { checkApolloError } from 'Helpers/graphql';
import { navigateBack } from 'Helpers/navigation';
import { APIErrorCodes } from 'app-types';
import { AddUserValues } from '../User.types';
import { getAddUserSubmitValues } from '../EditUser/EditUser.helpers';
import { AddUserRequest } from 'Services/Api/Users/Types';
import { useUser } from '../User.hooks';
import { updateUserCustomRoles } from '../service/index.Service';

const CreateUser = () => {
	const { t } = useTranslation();
	const history = useHistory();
	const { injectMeta } = useUser();
	const [createUser] = useMutation<
		{
			createUser: {
				peakV3Id: string | null;
				email: string | null;
			};
		},
		AddUserRequest
	>(CREATE_USER, {
		ignoreResults: false
	});
	const initialValues = {} as AddUserValues;

	const goBack = () => {
		navigateBack(history, getAppRoutes().USERS);
	};

	const onSubmit = async (
		values: AddUserValues,
		helpers: FormikHelpers<AddUserValues>
	) => {
		try {
			const result = await createUser({
				variables: {
					input: getAddUserSubmitValues(values)
				}
			});
			if (!result.data) {
				return;
			}
			
			const peakV3Id = result.data.createUser.peakV3Id;

			if (peakV3Id) {
				await updateUserCustomRoles(
					peakV3Id,
					values.customTenantRoleIds ?? []
				);
			}
			helpers.resetForm({ values });
			goBack();
			Notification.success({
				description: t('user.createUser.notification')
			});
		} catch (e) {
			const error = e as Error;
			helpers.setSubmitting(false);

			const errors = checkApolloError(error).getFieldErrors(
				t,
				injectMeta(schema.describe().fields),
				'user.errorCodes'
			);

			helpers.setErrors(
				(errors ?? {}) as unknown as FormikErrors<AddUserValues>
			);

			if (errors) return;

			const apolloError = checkApolloError(error);

			if (apolloError.is(APIErrorCodes.Forbidden)) {
				Notification.error({
					description: t(`errorCodes.${APIErrorCodes.Forbidden}`)
				});
			} else if (
				apolloError.is(APIErrorCodes.ValidationIsCognitoPhoneNumber)
			) {
				helpers.setErrors({
					phoneNumber: t(
						`user.errorCodes.${APIErrorCodes.ValidationIsCognitoPhoneNumber}`
					)
				});
			} else {
				Notification.error({
					description: t('errorCodes.genericErrorMessage')
				});
			}
		}
	};

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={onSubmit}
			validationSchema={schema}
		>
			{({ dirty, isSubmitting }) => (
				<>
					<Prompt blockTransition={dirty && !isSubmitting} />
					<Form>
						<EditableLayout
							goBack={goBack}
							heading={t('user.createUser.title')}
							okText={t('user.createUser.saveUser')}
						>
							<CreateUserFormFields />
						</EditableLayout>
					</Form>
				</>
			)}
		</Formik>
	);
};

export default CreateUser;
